import { AsterixOutlined, CheckmarkThickOutlined } from '@icon';
import classNames from 'classnames';
import hoistNonReactStatistics from 'hoist-non-react-statics';
import React from 'react';
import { useTheme } from 'styled-components';
import Form from '../../form';
import { TFormVariant } from '../../form/Form.types';
import { CheckboxSC } from './checkbox.styled';
import { changeEvent } from './checkbox.utils';

interface CheckboxProps<T>
  extends Omit<
    React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'ref' | 'children' | 'value'
  > {
  value: T;
  children?: React.ReactNode;
  variant?: TFormVariant;
  error?: boolean;
  required?: boolean;
}

const Checkbox = <T extends any>(
  props: CheckboxProps<T>,
  forwardedRef?: React.Ref<HTMLInputElement | null>,
) => {
  const { children, variant, className, style, value, onChange, error, required, ...rest } = props;
  const theme = useTheme();
  const settings = Form.useFormSettings({ variant });
  const innerRef = React.useRef<HTMLInputElement>(null);

  React.useImperativeHandle(forwardedRef, () => innerRef.current);

  return (
    <CheckboxSC
      disabled={props.disabled}
      readOnly={props.readOnly}
      error={error}
      className={classNames('ui-checkbox', className, {
        'ui-checkbox-children': !!children,
      })}
      style={style}
    >
      <label>
        <input type="checkbox" {...rest} ref={innerRef} value={value as any} onChange={onChange} />
        <span className="checkbox">
          <CheckmarkThickOutlined
            width={18}
            height={18}
            color={theme.color.tint.onSolid}
            className="ui-checkbox-mark"
          />
        </span>
      </label>
      {children || required ? (
        <div
          className="checkbox-text-wrapper"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            innerRef.current?.click();
          }}
        >
          {required ? (
            <AsterixOutlined color={theme.color.solid.error} width={16} height={16} />
          ) : null}
          {children}
        </div>
      ) : null}
    </CheckboxSC>
  );
};

Checkbox.changeEvent = changeEvent;

export default hoistNonReactStatistics(React.forwardRef(Checkbox), Checkbox) as typeof Checkbox;
